import React, { useState, useRef, useEffect } from "react";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { renderMarkdown } from "../lib/markdown";
import { toast } from "sonner";

/**
 * InlineToolChat — stateless conversational follow-up panel for AI tools whose
 * results are NOT persisted server-side (Source Triangulator, Question Mode).
 *
 * The parent passes:
 *   • `context`     — the original tool output as a plain string the LLM can
 *                     ground itself with (e.g. the triangulation markdown).
 *   • `sendChat`    — async (context, messages) => { reply } — the API call
 *                     to invoke. Different tools hit different endpoints; we
 *                     keep this component endpoint-agnostic.
 *   • `accent`      — hex color for the panel chrome so each tool keeps its
 *                     own visual identity.
 *   • `title`       — short label shown at the top of the panel.
 *   • `placeholder` — input placeholder text.
 *   • `testIdRoot`  — prefix for data-testid attributes (e.g. "triangulate-chat").
 *
 * The conversation lives entirely in component state — closing the page or
 * re-running the tool wipes it, which is the intended UX for these
 * lightweight, non-saveable tools.
 */
export default function InlineToolChat({
  context,
  sendChat,
  accent = "#ffd60a",
  title = "Ask a follow-up",
  placeholder = "Ask a follow-up question…",
  testIdRoot = "tool-chat",
}) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollAnchor = useRef(null);

  useEffect(() => {
    scrollAnchor.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, loading]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setDraft("");
    setLoading(true);
    try {
      const { reply } = await sendChat(context || "", next);
      setMessages([...next, { role: "assistant", content: reply || "_(no reply)_" }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Chat failed";
      toast.error(detail);
      setMessages([...next, { role: "assistant", content: `_Error: ${detail}_`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid={testIdRoot}
      className="mt-6 border bg-[#0a0a0a]"
      style={{ borderColor: `${accent}33` }}
    >
      <div
        className="px-4 py-2 flex items-center justify-between border-b"
        style={{ borderColor: `${accent}33` }}
      >
        <div className="flex items-center gap-2">
          <MessageSquare size={12} style={{ color: accent }} />
          <span className="font-mono text-[10px] tracking-[0.3em]" style={{ color: accent }}>
            // {title.toUpperCase()}
          </span>
        </div>
        <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">
          {messages.length} {messages.length === 1 ? "TURN" : "TURNS"}
        </span>
      </div>

      {messages.length > 0 && (
        <div className="px-4 py-4 space-y-4 max-h-[600px] overflow-y-auto" data-testid={`${testIdRoot}-messages`}>
          {messages.map((m, i) => {
            const isUser = m.role === "user";
            return (
              <div
                key={i}
                data-testid={`${testIdRoot}-msg-${i}`}
                className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
              >
                <div
                  className="font-mono text-[9px] tracking-[0.25em] mb-1 text-zinc-500"
                >
                  {isUser ? "YOU" : "ANALYST"}
                </div>
                <div
                  className={`max-w-[88%] px-3 py-2 text-[13px] leading-relaxed ${isUser ? "bg-white/5 text-white" : "bg-[#0F0F0F] text-zinc-200"}`}
                  style={{
                    border: `1px solid ${isUser ? "rgba(255,255,255,0.12)" : `${accent}33`}`,
                  }}
                >
                  {isUser ? (
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  ) : (
                    <div
                      className="markdown markdown-compact"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content || "") }}
                    />
                  )}
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="flex items-center gap-2 text-zinc-500 font-mono text-[10px] tracking-[0.25em]" data-testid={`${testIdRoot}-thinking`}>
              <Loader2 size={11} className="animate-spin" /> ANALYST DRAFTING REPLY…
            </div>
          )}
          <div ref={scrollAnchor} />
        </div>
      )}

      <form
        onSubmit={submit}
        className="px-4 py-3 flex gap-2 border-t"
        style={{ borderColor: `${accent}33` }}
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testIdRoot}-input`}
          disabled={loading}
          className="flex-1 bg-[#050505] border border-white/15 px-3 py-2 font-mono text-sm text-white placeholder:text-zinc-600 focus:outline-none"
          style={{ borderColor: `${accent}55` }}
        />
        <button
          type="submit"
          disabled={loading || !draft.trim()}
          data-testid={`${testIdRoot}-send`}
          className="inline-flex items-center gap-1.5 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] disabled:opacity-40"
          style={{ background: accent, color: "#050505" }}
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
          {loading ? "WAIT" : "SEND"}
        </button>
      </form>
    </section>
  );
}
